import React, { useState } from 'react';
import { Upload, Link, Send, Loader2, AlertCircle, X, FileVideo, Facebook, Video, Instagram, Youtube, Twitter, Cpu, Zap, Brain, Lock, CheckCircle, Award, Users, Target } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import ProgressAnimation from './ProgressAnimation';

const VideoUploadForm = ({ onAnalysisComplete, onAnalysisError, isLoading, setIsLoading }) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    postUrl: '',
    platform: 'meta',
    includeTranscript: true,
    geminiModel: 'gemini-2.5-flash-lite',
    reportType: 'standard'
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [showAdvancedMode, setShowAdvancedMode] = useState(false);

  const platforms = [
    { id: 'meta', name: 'Meta (FB & Insta)', color: 'bg-blue-500' },
    { id: 'tiktok', name: 'TikTok', color: 'bg-black' },
    { id: 'instagram', name: 'Instagram', color: 'bg-pink-500' },
    { id: 'youtube', name: 'YouTube', color: 'bg-red-500' },
    { id: 'twitter', name: 'Twitter/X', color: 'bg-blue-400' }
  ];

  const geminiModels = [
    { 
      id: 'gemini-2.5-pro', 
      name: 'Gemini 2.5 Pro', 
      description: 'Highest Quality (Slow)',
      icon: <Brain className="w-4 h-4" />,
      color: 'bg-purple-500',
      recommended: false
    },
    { 
      id: 'gemini-2.5-flash', 
      name: 'Gemini 2.5 Flash', 
      description: 'Quality & Speed',
      icon: <Cpu className="w-4 h-4" />,
      color: 'bg-blue-500',
      recommended: false
    },
    { 
      id: 'gemini-2.5-flash-lite', 
      name: 'Gemini 2.5 Flash Lite', 
      description: 'Speed',
      icon: <Zap className="w-4 h-4" />,
      color: 'bg-green-500',
      recommended: false
    }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!selectedFile) {
      newErrors.file = 'Video file is required';
    }

    if (formData.postUrl && !isValidUrl(formData.postUrl)) {
      newErrors.postUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
      setErrors(prev => ({ ...prev, file: '' }));
      toast.success(`Selected: ${file.name}`);
    } else {
      toast.error('Please select a valid video file');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);
    setCurrentStep(1);

    // Realistic progress steps with actual upload and processing
    const progressSteps = [
      { step: 1, delay: 500 },   // Upload preparation
      { step: 2, delay: 1000 },  // File upload
      { step: 3, delay: 2000 },  // Video processing
      { step: 4, delay: 3000 },  // AI analysis
      { step: 5, delay: 2000 },  // Score generation
      { step: 6, delay: 1000 }   // Report finalization
    ];

    try {
      // Start progress animation
      for (const { step, delay } of progressSteps) {
        await new Promise(resolve => {
          setTimeout(() => {
            setCurrentStep(step);
            resolve();
          }, delay);
        });
      }

      const formDataToSend = new FormData();
      formDataToSend.append('video', selectedFile);
      formDataToSend.append('postUrl', formData.postUrl || '');
      formDataToSend.append('platform', formData.platform || '');
      formDataToSend.append('includeTranscript', formData.includeTranscript.toString());
      formDataToSend.append('geminiModel', formData.geminiModel);
      formDataToSend.append('reportType', formData.reportType);

      const response = await fetch('/api/analyze-upload', {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      toast.success('Video analysis completed successfully!');
      onAnalysisComplete(data);
    } catch (error) {
      console.error('Video analysis error:', error);
      
      // Try to parse error response
      let errorData = { error: error.message };
      try {
        if (error.response) {
          errorData = await error.response.json();
        }
      } catch (parseError) {
        // Ignore parse error, use default error message
      }
      
      if (errorData?.error) {
        onAnalysisError(errorData);
      } else {
        const errorMessage = errorData?.error || 'Failed to analyze video';
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
      setCurrentStep(0);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  return (
    <>
      {isLoading && <ProgressAnimation currentStep={currentStep} />}

      {!currentUser ? (
        <>
          {/* Sign In Required Screen */}
          <section className="max-w-3xl mx-auto mb-20">
          {/* Background effect container */}
          <div className="relative">
            {/* Glowing background effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 rounded-3xl blur-xl opacity-20 animate-pulse"></div>
            <div className="absolute -inset-2 bg-gradient-to-r from-purple-300 via-pink-300 to-blue-300 rounded-2xl blur-lg opacity-30"></div>
            
            {/* Main form container */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden backdrop-blur-sm">
            <div className="grid md:grid-cols-2">
              {/* Left Side - Header */}
              <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600 p-6 flex flex-col justify-center text-center md:text-left relative">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="relative z-10">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto md:mx-0 mb-3 backdrop-blur-sm">
                    <Lock className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">
                    Sign In Required
                  </h2>
                  <p className="text-purple-100 text-xs mb-4">
                    Please sign in to access video analysis
                  </p>
                  <div className="flex flex-col space-y-1.5 text-xs text-white/90">
                    <div className="flex items-center justify-center md:justify-start">
                      <CheckCircle className="w-3 h-3 mr-1.5" />
                      Qualify Your Ads Before Publishing
                    </div>
                    <div className="flex items-center justify-center md:justify-start">
                      <CheckCircle className="w-3 h-3 mr-1.5" />
                      Save Wasted Ad Spend
                    </div>
                    <div className="flex items-center justify-center md:justify-start">
                      <CheckCircle className="w-3 h-3 mr-1.5" />
                      Increase ROI
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Side - Sign In Actions */}
              <div className="p-6 flex flex-col justify-center">
                <p className="text-gray-600 mb-4 text-sm text-center">
                  Create an account or sign in to start analyzing your videos with AI-powered insights.
                </p>
                
                <div className="space-y-3">
              <button
                onClick={() => {
                  // Dispatch custom event to trigger sign-in modal
                  window.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'login' } }));
                }}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Sign In to Continue
              </button>
                  <p className="text-xs text-gray-500 text-center">
                    Haven't signed up yet?{' '}
                    <button
                      onClick={() => {
                        // Dispatch custom event to trigger sign-up modal
                        window.dispatchEvent(new CustomEvent('openAuthModal', { detail: { mode: 'signup' } }));
                      }}
                      className="text-purple-600 hover:text-purple-700 font-medium underline"
                    >
                      Sign up here
                    </button>
                    {' '}• No credit card required
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </section>
        </>
      ) : (
        // Main Upload Form
        <section className="max-w-3xl mx-auto mb-16">
        {/* Background effect container */}
        <div className="relative">
          {/* Glowing background effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 rounded-3xl blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -inset-2 bg-gradient-to-r from-purple-300 via-pink-300 to-blue-300 rounded-2xl blur-lg opacity-30"></div>
          
          {/* Main form container */}
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden backdrop-blur-sm">
          {/* Gradient Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 text-center relative">
            <div className="absolute inset-0 bg-black opacity-10"></div>
            <div className="relative z-10">
              <h2 className="text-xl font-bold text-white mb-1">
                Upload Video for Analysis
              </h2>
              <p className="text-indigo-100 text-xs">
                Get AI-powered insights in seconds
              </p>
            </div>
            {/* Decorative elements */}
            <div className="absolute top-4 left-4 w-2 h-2 bg-white bg-opacity-30 rounded-full"></div>
            <div className="absolute top-8 right-8 w-1 h-1 bg-white bg-opacity-40 rounded-full"></div>
            <div className="absolute bottom-6 left-12 w-1.5 h-1.5 bg-white bg-opacity-30 rounded-full"></div>
            <div className="absolute bottom-4 right-4 w-2 h-2 bg-white bg-opacity-20 rounded-full"></div>
          </div>
          
          <div className="p-5">

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Video Upload */}
            <div>
              
              {!selectedFile ? (
                <div
                  className={`border-2 border-dashed rounded-xl p-6 text-center transition-all duration-300 transform ${
                    dragActive 
                      ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 scale-105 shadow-lg' 
                      : 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50 hover:scale-102'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {dragActive ? 'Drop your video here!' : 'Drop your video here or click to browse'}
                  </h3>
                  <p className="text-gray-600 mb-3 max-w-md mx-auto text-xs">
                    Supports MP4, AVI, MOV, WMV, WebM up to 50MB
                  </p>
                  
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                    className="hidden"
                    id="video-upload"
                    name="video-upload"
                  />
                  <label
                    htmlFor="video-upload"
                    className="inline-flex items-center px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer text-sm"
                  >
                    <Upload className="w-5 h-5 mr-2" />
                    Choose Video File
                  </label>
                </div>
              ) : (
                <div className="border border-indigo-200 rounded-xl p-4 bg-gradient-to-r from-indigo-50 to-purple-50 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                        <FileVideo className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-lg">{selectedFile.name}</p>
                        <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="w-10 h-10 bg-red-100 hover:bg-red-200 text-red-600 hover:text-red-700 rounded-full flex items-center justify-center transition-all duration-200 transform hover:scale-110"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="mt-4 flex items-center text-sm text-indigo-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span className="font-medium">Ready for analysis</span>
                  </div>
                </div>
              )}
              
              {errors.file && (
                <div className="flex items-center mt-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.file}
                </div>
              )}
            </div>


            {/* Advanced Mode Toggle */}
            <div className="hidden md:flex items-center justify-center py-3">
              <button
                type="button"
                onClick={() => {
                  setShowAdvancedMode(!showAdvancedMode);
                  // Smooth scroll to keep the button in view
                  setTimeout(() => {
                    const button = document.activeElement;
                    if (button && typeof button.scrollIntoView === 'function') {
                      button.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                  }, 100);
                }}
                className="flex items-center space-x-2 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200 hover:border-gray-300"
              >
                <span>{showAdvancedMode ? 'Hide' : 'Show'} Advanced Options</span>
                <svg 
                  className={`w-5 h-5 transition-transform duration-200 ${showAdvancedMode ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Advanced Options - Always Rendered with CSS Transitions */}
            <div className={`hidden md:block overflow-hidden transition-all duration-300 ease-in-out ${
              showAdvancedMode 
                ? 'max-h-[800px] opacity-100' 
                : 'max-h-0 opacity-0'
            }`}>
                {/* Report Type Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Select Report Type
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label
                      className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.reportType === 'standard'
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reportType"
                        value="standard"
                        checked={formData.reportType === 'standard'}
                        onChange={(e) => handleInputChange('reportType', e.target.value)}
                        className="sr-only"
                        disabled={isLoading}
                      />
                      <div className="flex items-center w-full">
                        <div className="w-8 h-8 rounded-lg mr-3 flex items-center justify-center bg-blue-500">
                          <span className="text-white text-sm font-bold">S</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-800">Standard</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Best for Short Videos (6-15secs)</p>
                        </div>
                      </div>
                    </label>
                    
                    <label
                      className={`relative flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.reportType === 'professional'
                          ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reportType"
                        value="professional"
                        checked={formData.reportType === 'professional'}
                        onChange={(e) => handleInputChange('reportType', e.target.value)}
                        className="sr-only"
                        disabled={isLoading}
                      />
                      <div className="flex items-center w-full">
                        <div className="w-8 h-8 rounded-lg mr-3 flex items-center justify-center bg-purple-500">
                          <span className="text-white text-sm font-bold">P</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-800">Professional</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Advanced Multi-Framework Analysis</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Gemini Model Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    AI Model Selection
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {geminiModels.map((model) => (
                      <label
                        key={model.id}
                        className={`relative flex items-center p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          formData.geminiModel === model.id
                            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="geminiModel"
                          value={model.id}
                          checked={formData.geminiModel === model.id}
                          onChange={(e) => handleInputChange('geminiModel', e.target.value)}
                          className="sr-only"
                          disabled={isLoading}
                        />
                        <div className="flex items-center w-full">
                          <div className={`w-8 h-8 rounded-lg mr-3 flex items-center justify-center ${model.color}`}>
                            {model.icon}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-800">{model.name}</span>
                              {model.recommended && (
                                <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                  Recommended
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{model.description}</p>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Platform Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Select Platform for Best Practices
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map((platform) => (
                      <label
                        key={platform.id}
                        className={`relative flex items-center px-3 py-2 border rounded-lg cursor-pointer transition-all duration-200 ${
                          formData.platform === platform.id
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="platform"
                          value={platform.id}
                          checked={formData.platform === platform.id}
                          onChange={(e) => handleInputChange('platform', e.target.value)}
                          className="sr-only"
                          disabled={isLoading}
                        />
                        <div className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${platform.color}`}>
                          {platform.id === 'meta' && (
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                              <defs>
                                <linearGradient id="meta-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" style={{stopColor:"#1877f2", stopOpacity:"1"}} />
                                  <stop offset="100%" style={{stopColor:"#42a5f5", stopOpacity:"1"}} />
                                </linearGradient>
                              </defs>
                              <path 
                                d="M8 8c0-2.21 1.79-4 4-4s4 1.79 4 4-1.79 4-4 4-4-1.79-4-4z M16 16c0-2.21-1.79-4-4-4s-4 1.79-4 4 1.79 4 4 4 4-1.79 4-4z M8 16c0 2.21-1.79 4-4 4s-4-1.79-4-4 1.79-4 4-4 4 1.79 4 4z M16 8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.21-1.79 4-4 4s-4-1.79-4-4z"
                                fill="url(#meta-gradient)" 
                                stroke="none"
                              />
                            </svg>
                          )}
                          {platform.id === 'tiktok' && <Video className="w-3 h-3 text-white" />}
                          {platform.id === 'instagram' && <Instagram className="w-3 h-3 text-white" />}
                          {platform.id === 'youtube' && <Youtube className="w-3 h-3 text-white" />}
                          {platform.id === 'twitter' && <Twitter className="w-3 h-3 text-white" />}
                        </div>
                        <span className="text-sm font-medium text-gray-800">{platform.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Post URL (Optional) */}
                <div className="mb-6">
                  <label htmlFor="postUrl" className="block text-sm font-bold text-gray-700 mb-3">
                    Post URL (Optional) - For engagement data
                  </label>
                  <div className="relative">
                    <input
                      type="url"
                      id="postUrl"
                      value={formData.postUrl}
                      onChange={(e) => handleInputChange('postUrl', e.target.value)}
                      placeholder="https://www.tiktok.com/@username/video/... (for likes, comments, hashtags)"
                      className={`input-field pr-12 ${errors.postUrl ? 'border-red-500 focus:ring-red-500' : ''}`}
                      disabled={isLoading}
                    />
                    <Link className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  {errors.postUrl && (
                    <div className="flex items-center mt-2 text-sm text-red-600">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.postUrl}
                    </div>
                  )}
                </div>

                {/* Additional Options */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="includeTranscript"
                      name="includeTranscript"
                      checked={formData.includeTranscript}
                      onChange={(e) => handleInputChange('includeTranscript', e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      disabled={isLoading}
                    />
                    <label htmlFor="includeTranscript" className="ml-2 text-sm text-gray-700">
                      Include transcript analysis (if available)
                    </label>
                  </div>
                  
                </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-4">
              <button
                type="submit"
                className="group relative w-[40%] py-2.5 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-bold text-sm rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                disabled={isLoading || !selectedFile}
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {isLoading ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin mr-3 relative z-10" />
                    <span className="relative z-10">Analysing Video...</span>
                  </>
                ) : (
                  <>
                    <span className="relative z-10">Analyse Video</span>
                  </>
                )}
                
                {/* Shimmer effect */}
                <div className="absolute inset-0 -skew-x-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 group-hover:animate-pulse"></div>
              </button>
            </div>
            
            {/* AI Disclaimer */}
            <div className="mt-3 text-center">
              <p className="text-xs text-gray-500">
                AI is experimental and can make mistakes. Always double check its responses.
              </p>
            </div>
          </form>

          </div>
        </div>
        </div>
      </section>
      )}
    </>
  );
};

export default VideoUploadForm;
